<script setup lang="ts">
import { computed, ref } from 'vue';
import download from 'downloadjs';
import FileUpload, { FileUploadRemoveEvent } from 'primevue/fileupload';
import Dialog from 'primevue/dialog';
import useFileList from '@/composables/useFileList';
import FilePreview from './components/FilePreview.vue';
import { EMIT } from '@/enum';
import { FileInterface } from '@/types';
import { useStudentStore } from '@/store';
import { StudentService } from '@/services';

const emit = defineEmits<{
  (eventName: EMIT.FILE_DROPPED, files: string[]): void;
  (eventName: EMIT.SAVE): void;
  (eventName: EMIT.CANCEL): void;
  (eventName: EMIT.ERROR, error: string): void;
}>();

const studentStore = useStudentStore();

const { files, addFiles, removeFile } = useFileList();

const modalVisible = ref(true);

const createForm = (file: FileInterface) => {
  const formData = new FormData();

  formData.append('document', file.file);
  formData.append('name', file.name);

  return formData;
};

const save = async () => {
  const file: FileInterface = [...files.value][0];
  const formData = createForm(file);

  await StudentService.importStudentIEPRequest(formData)
    .then((response) => {
      modalVisible.value = false;
      emit(EMIT.SAVE, response.data.message, 'Import');
    })
    .catch((error) => {
      const message = Object.values(error.data.errors).flat().values().next().value as string;

      emit(EMIT.ERROR, message ?? 'Something went wrong');
      removeFile(file);
    });
};

const onDrop = (e: FileUploadRemoveEvent) => addFiles(e.files);

const formDisabled = computed<boolean>(() => ![...files.value].length);

const cancel = () => {
  removeError();
  emit(EMIT.CANCEL);
  modalVisible.value = false;
};

const removeError = () => (studentStore.error = undefined);

const doDownload = async () => {
  const response = await StudentService.getStudentIEPImportTemplate();
  const content = response.headers['content-type'];

  download(response.data, 'student-iep-template.csv', content);
};
</script>

<template>
  <Dialog
    v-model:visible="modalVisible"
    class="dialog__container add-document__modal"
    content-class="pt-2"
    :modal="true"
    :draggable="false"
    @hide="cancel"
  >
    <template #header>
      <div class="dialog__header flex flex-column">
        <h2 class="m-0">Import IEPs</h2>
      </div>
    </template>
    <div class="flex text-teal-500 cursor-pointer" @click.stop.prevent="doDownload">
      <Icon name="insert_drive_file" />
      <span class="underline mt-1 ml-2 text-sm">Download Template</span>
    </div>
    <div class="mt-3">
      <div class="upload-wrapper flex relative text-center align-items-center justify-content-center w-full">
        <FileUpload
          v-if="!files.length"
          :custom-upload="true"
          class="upload relative"
          choose-label="Choose file"
          accept=".csv, .xlsx"
          :invalid-file-type-message="'Sorry, this file type is not supported. Please select a supported file type: {1}'"
          :auto="true"
          mode="advanced"
          :show-cancel-button="false"
          :show-upload-button="false"
          :max-file-size="100000000"
          invalid-file-size-message="Error: This file exceeds the max file size allowed."
          @uploader="onDrop"
        >
          <template #empty>
            <p class="uploader-text font-bold text-base mt-2">or drag and drop here</p>
            <p class="acceptable-files-text">Allowed types: .csv</p>
            <p class="acceptable-files-text">Max File size: 100MB</p>
          </template>
        </FileUpload>

        <ul v-else class="upload-wrapper__file-list flex w-full h-full list-none absolute left-0 align-items-center">
          <FilePreview
            v-for="file of files"
            :key="file.id"
            class="w-full flex text-left align-items-center"
            :file="file"
            tag="li"
            @remove="removeFile"
          />
        </ul>
      </div>
    </div>
    <template #footer>
      <AppButton type="secondary" text="Cancel" @click="cancel" />
      <AppButton type="primary" text="Import Users IEP Information" :disabled="formDisabled" @click="save" />
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.dialog__container {
  .p-dialog-content {
    margin-top: 0;

    .upload-wrapper {
      height: 98px;
      padding-top: 0;
      margin-top: 0;
      align-items: flex-start;

      :deep(.p-fileupload-buttonbar) {
        padding-top: 0;
        background: none;
        border: none;

        .p-button {
          margin-left: -94%;
          padding: 4px;
          padding-top: 0;
          margin-top: 4px;
          font-weight: bold;
          cursor: pointer;
          z-index: 5;
          border: none;

          .p-button-label {
            text-decoration: underline;
            font-size: 16px;
          }
          .p-button-icon {
            display: none;
          }
        }
      }
      :deep(.p-fileupload-content) {
        text-align: center;
        border: 2px dashed $grayMedium;
        border-radius: 8px;
        height: 98px;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
        background: transparent;
        display: flex;
        justify-content: center;

        .p-message-error {
          position: absolute;
          top: -51px;
          width: 100%;
        }
        .p-fileupload-empty {
          position: relative;
          top: -10px;
          .uploader-text {
            color: var(--primary-color-dark);
            line-height: 18px;
            margin-bottom: 4px;
            margin-right: -40%;
          }
          .acceptable-files-text {
            font-size: 10px;
            line-height: 12px;
            color: var(--primary-color-dark);
          }
        }
      }
      :deep(.p-fileupload-highlight) {
        border-color: var(--primary-color);
      }

      .upload-wrapper__file-list {
        color: $grayDark;
        font-size: 14px;
        line-height: 18px;
        border: 2px dashed $grayMedium;
        border-radius: 8px;
        height: 98px;
        padding: 1rem 2rem;
        top: -14px;
      }

      .v-enter-active,
      .v-leave-active {
        transition: opacity 0.5s ease;
      }
      .v-enter-from,
      .v-leave-to {
        opacity: 0;
      }
      .upload-wrapper__error {
        color: #822508;
        top: -65px;
        height: 37px;
        z-index: 6;
      }
    }

    .description-field {
      margin-top: 6px;
      margin-bottom: 6px;

      .description-field__input {
        margin: 2px 0;
      }
    }
    .tree {
      width: 10px;
    }
  }
}
</style>
